import {
  Box,
  Collapse,
  Flex,
  FormControl,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react"
import { format } from "date-fns"
import { Controller, useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import ReactSelect from "react-select"

import { NextStepButton } from "@/components/FormButtons/NextStepButton"
import SingleField from "@/components/Sections/SingleField"
import { useStepperContext } from "@/components/Stepper/StepperContext"
import {
  PEOPLE_CONTACT_METHOD_GROUP,
  PEOPLE_MARITAL_CONTRACT_STATUS_GROUP,
  PEOPLE_MARITAL_STATUS_GROUP,
  PEOPLE_TITLE_GROUP,
} from "@/domain/people/constants"
import { useGetRefLabels } from "@/networking/api"
import { get_t11n_label } from "@/utils/i18n/t11n"

import { ButtonWithIcon } from "@/components/Buttons/ButtonWithIcon"
import FormattedErrorMessage from "@/components/FormattedErrorMessage/FormattedErrorMessage"
import { MinusIcon, PlusIcon } from "@/components/Icons"
import { MultipleField, MultipleFieldColumn } from "@/components/Sections/MultipleField"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import useLoadRefLabels from "@/hooks/useLoadRefLabels"
import { useGetReactSelectStyles } from "@/theme/components-style-overrides/reactselect-definition"
import { fieldRenderForSmallField } from "@/theme/custom"
import { Orders } from "@/utils/filters/types"
import { isNotBlank } from "@/utils/ts-utils"
import { useAddOrEditPeopleStepFormContext } from "../../context/AddOrEditPeopleFormStepContext"
import type { AddPeopleType, EditPeopleType } from "../../helpers/AddPeopleData"

export const GeneralStep: React.FC = () => {
  const intl = useIntl()
  const { goToNextStep } = useStepperContext()

  const { clusters, formStep1State, setFormStep1State } = useAddOrEditPeopleStepFormContext()
  const { defaultClusterId } = useGetUserInfos()

  // Select / option for clusters : required before formSate def
  const clustersOptions = clusters?.map(item => ({
    label: item.name,
    value: item.id,
  }))
  const peopleClusterId =
    formStep1State.fid_cluster ??
    (isNotBlank(defaultClusterId) && (clusters ?? []).map(c => c.id).includes(defaultClusterId)
      ? defaultClusterId
      : (clusters?.[0]?.id ?? ""))

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddPeopleType & EditPeopleType>({
    defaultValues: {
      ...formStep1State,
      fid_cluster: peopleClusterId,
      birthdate: isNotBlank(formStep1State?.birthdate)
        ? format(new Date(formStep1State?.birthdate), "yyyy-MM-dd")
        : "",
    },
  })

  const { isOpen: isMoreFieldsOpen, onToggle: onMoreFieldsToggle } = useDisclosure()

  // Select / Options

  const {
    data: titles,
    isFetching: isFetchingTitles,
    isLoading: isLoadingTitles,
  } = useLoadRefLabels(PEOPLE_TITLE_GROUP, {
    "order[order]": Orders.DESC,
    "order[label]": Orders.ASC,
  })
  /*useGetRefLabels({
    group: PEOPLE_TITLE_GROUP,
  })*/
  const titleOptions = titles?.["hydra:member"].map(item => ({
    label: get_t11n_label(item, intl),
    value: item.code,
  }))

  const {
    data: contactMethods,
    isFetching: isFetchingContactMethods,
    isLoading: isLoadingContactMethods,
  } = useGetRefLabels({
    group: PEOPLE_CONTACT_METHOD_GROUP,
  })
  const contactMethodsOptions = contactMethods?.["hydra:member"].map(item => ({
    label: get_t11n_label(item, intl),
    value: item.code,
  }))

  const {
    data: maritalStatus,
    isFetching: isFetchingMaritalStatus,
    isLoading: isLoadingMaritalStatus,
  } = useGetRefLabels({
    group: PEOPLE_MARITAL_STATUS_GROUP,
  })
  const maritalStatusOptions = maritalStatus?.["hydra:member"].map(item => ({
    label: get_t11n_label(item, intl),
    value: item.code,
  }))

  const {
    data: maritalContractStatus,
    isFetching: isFetchingMaritalContractStatus,
    isLoading: isLoadingMaritalContractStatus,
  } = useGetRefLabels({
    group: PEOPLE_MARITAL_CONTRACT_STATUS_GROUP,
  })
  const maritalContractStatusOptions = maritalContractStatus?.["hydra:member"].map(item => ({
    label: get_t11n_label(item, intl),
    value: item.code,
  }))

  const {
    data: languages,
    isFetching: isFetchingLangs,
    isLoading: isLoadingLangs,
  } = useGetRefLabels({
    group: "locale:",
    itemsPerPage: 500,
  })
  const languageOptions = languages?.["hydra:member"].map(item => ({
    label: get_t11n_label(item, intl),
    value: item.code,
  }))

  const {
    data: nationalities,
    isFetching: isFetchingNationalities,
    isLoading: isLoadingNationalities,
  } = useGetRefLabels({
    group: "country:",
    itemsPerPage: 500,
  })
  const nationalitiesOptions = nationalities?.["hydra:member"].map(item => ({
    label: get_t11n_label(item, intl),
    value: item.code,
  }))

  const onSubmit = (data: AddPeopleType & EditPeopleType) => {
    setFormStep1State(prevFormState => ({
      ...prevFormState,
      ...data,
      childCount: Number(data.childCount) ?? undefined,
      maritalContractStatus: data?.maritalContractStatus ?? "",
      maritalStatus: data?.maritalStatus ?? "",
      nationality: data?.nationality ?? "",
      title: data?.title ?? "",
    }))

    goToNextStep()
  }

  const waitBeforeNextStep =
    isFetchingTitles ||
    isLoadingTitles ||
    isFetchingContactMethods ||
    isLoadingContactMethods ||
    isFetchingMaritalStatus ||
    isLoadingMaritalStatus ||
    isFetchingMaritalContractStatus ||
    isLoadingMaritalContractStatus ||
    isFetchingLangs ||
    isLoadingLangs ||
    isFetchingNationalities ||
    isLoadingNationalities

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} mb="4rem">
      <Flex direction="column" gap="5" my="8">
        {(clustersOptions?.length ?? 0) > 1 && (
          <SingleField
            label={intl.formatMessage({
              id: "client.modalEdit.generalStep.whichCluster",
            })}
          >
            <FormControl isInvalid={!!errors.fid_cluster}>
              <Controller
                control={control}
                name="fid_cluster"
                rules={{ required: true }}
                render={({ field: { value, onChange }, fieldState }) => (
                  <ReactSelect
                    options={clustersOptions}
                    value={clustersOptions?.filter(opt => opt.value === value).shift()}
                    onChange={newVal => onChange(newVal?.value)}
                    // isLoading={isFetchingClusters || isLoadingClusters}
                    styles={useGetReactSelectStyles(fieldState)}
                  />
                )}
              />
            </FormControl>
          </SingleField>
        )}

        <SingleField
          label={intl.formatMessage({
            id: "client.modalEdit.generalStep.gender",
          })}
          required={true}
          {...fieldRenderForSmallField}
        >
          <FormControl isInvalid={!!errors.title}>
            <Controller
              control={control}
              name="title"
              rules={{ required: true }}
              render={({ field: { value, onChange }, fieldState }) => (
                <ReactSelect
                  options={titleOptions}
                  value={titleOptions?.filter(opt => opt.value === value).shift()}
                  onChange={newVal => onChange(newVal?.value)}
                  isLoading={isFetchingTitles || isLoadingTitles}
                  styles={useGetReactSelectStyles(fieldState)}
                />
              )}
            />
            <FormattedErrorMessage fieldError={errors.title} />
          </FormControl>
        </SingleField>

        <SingleField
          label={intl.formatMessage({
            id: "client.modalEdit.generalStep.firstname",
          })}
          required={true}
        >
          <FormControl isInvalid={!!errors.firstname}>
            <Input {...register("firstname", { required: true })} />
            <FormattedErrorMessage fieldError={errors.firstname} />
          </FormControl>
        </SingleField>

        <SingleField
          label={intl.formatMessage({
            id: "client.modalEdit.generalStep.lastname",
          })}
          required={true}
        >
          <FormControl isInvalid={!!errors.lastname}>
            <Input {...register("lastname", { required: true })} />
            <FormattedErrorMessage fieldError={errors.lastname} />
          </FormControl>
        </SingleField>

        <MultipleField label={intl.formatMessage({ id: "client.modalEdit.generalStep.l10n" })}>
          <MultipleFieldColumn
            label={intl.formatMessage({
              id: "client.modalEdit.generalStep.nationality",
            })}
          >
            <FormControl isInvalid={!!errors.nationality}>
              <Controller
                control={control}
                name="nationality"
                render={({ field: { value, onChange }, fieldState }) => (
                  <ReactSelect
                    options={nationalitiesOptions}
                    value={nationalitiesOptions?.filter(opt => opt.value === value).shift()}
                    onChange={newVal => onChange(newVal?.value)}
                    isLoading={isFetchingNationalities || isLoadingNationalities}
                    styles={useGetReactSelectStyles(fieldState)}
                  />
                )}
              />
              <FormattedErrorMessage fieldError={errors.nationality} />
            </FormControl>
          </MultipleFieldColumn>
          <MultipleFieldColumn
            label={intl.formatMessage({
              id: "client.modalEdit.generalStep.locale",
            })}
            required={true}
          >
            <FormControl isInvalid={!!errors.locale}>
              <Controller
                control={control}
                name="locale"
                rules={{ required: true }}
                render={({ field: { value, onChange }, fieldState }) => (
                  <ReactSelect
                    isMulti
                    value={languageOptions?.filter(opt => (value ?? []).includes(opt.value ?? ""))}
                    options={languageOptions}
                    onChange={newVal => onChange(newVal.map(option => option.value))}
                    isLoading={isFetchingLangs || isLoadingLangs}
                    styles={useGetReactSelectStyles(fieldState)}
                  />
                )}
              />
              <FormattedErrorMessage fieldError={errors.locale} />
            </FormControl>
          </MultipleFieldColumn>
        </MultipleField>

        <Box mt="0" mb="4">
          <ButtonWithIcon
            onClick={onMoreFieldsToggle}
            icon={isMoreFieldsOpen ? MinusIcon : PlusIcon}
            iconColor="accent"
          >
            <FormattedMessage
              id={isMoreFieldsOpen ? "form.displayLessFields" : "form.displayMoreFields"}
            />
          </ButtonWithIcon>
          <Collapse in={isMoreFieldsOpen} animateOpacity style={{ overflow: "visible" }}>
            <Flex direction="column" gap="5" mt="8">
              <MultipleField
                label={intl.formatMessage({ id: "client.modalEdit.generalStep.birthinfos" })}
              >
                <MultipleFieldColumn
                  label={intl.formatMessage({
                    id: "client.modalEdit.generalStep.birthdate",
                  })}
                >
                  <FormControl isInvalid={!!errors.birthdate}>
                    <Input {...register("birthdate", { required: false })} type="date" />
                    <FormattedErrorMessage fieldError={errors.birthdate} />
                  </FormControl>
                </MultipleFieldColumn>
                <MultipleFieldColumn
                  label={intl.formatMessage({
                    id: "client.modalEdit.generalStep.birthplace",
                  })}
                >
                  <FormControl isInvalid={!!errors.birthplace}>
                    <Input {...register("birthplace", { required: false })} />
                    <FormattedErrorMessage fieldError={errors.birthplace} />
                  </FormControl>
                </MultipleFieldColumn>
              </MultipleField>

              <MultipleField
                label={intl.formatMessage({ id: "client.modalEdit.generalStep.familySituation" })}
              >
                <MultipleFieldColumn
                  label={intl.formatMessage({
                    id: "client.modalEdit.generalStep.maritalStatus",
                  })}
                >
                  <FormControl isInvalid={!!errors.maritalStatus}>
                    <Controller
                      control={control}
                      name="maritalStatus"
                      render={({ field: { value, onChange }, fieldState }) => (
                        <ReactSelect
                          options={maritalStatusOptions}
                          value={maritalStatusOptions?.filter(opt => opt.value === value).shift()}
                          onChange={newVal => onChange(newVal?.value)}
                          isLoading={isFetchingMaritalStatus || isLoadingMaritalStatus}
                          styles={useGetReactSelectStyles(fieldState)}
                        />
                      )}
                    />
                    <FormattedErrorMessage fieldError={errors.maritalStatus} />
                  </FormControl>
                </MultipleFieldColumn>
                <MultipleFieldColumn
                  label={intl.formatMessage({
                    id: "client.modalEdit.generalStep.maritalContractStatus",
                  })}
                >
                  <FormControl isInvalid={!!errors.maritalContractStatus}>
                    <Controller
                      control={control}
                      name="maritalContractStatus"
                      render={({ field: { value, onChange }, fieldState }) => (
                        <ReactSelect
                          options={maritalContractStatusOptions}
                          value={maritalContractStatusOptions
                            ?.filter(opt => opt.value === value)
                            .shift()}
                          onChange={newVal => onChange(newVal?.value)}
                          isLoading={
                            isFetchingMaritalContractStatus || isLoadingMaritalContractStatus
                          }
                          styles={useGetReactSelectStyles(fieldState)}
                        />
                      )}
                    />
                    <FormattedErrorMessage fieldError={errors.maritalContractStatus} />
                  </FormControl>
                </MultipleFieldColumn>
              </MultipleField>

              <Flex direction="row" gap="10" m="0">
                <SingleField
                  label={intl.formatMessage({
                    id: "client.modalEdit.generalStep.children",
                  })}
                  {...fieldRenderForSmallField}
                >
                  <FormControl isInvalid={!!errors.childCount}>
                    <NumberInput size="sm" min={0} max={999}>
                      <NumberInputField {...register("childCount", { required: false })} />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormattedErrorMessage fieldError={errors.childCount} />
                  </FormControl>
                </SingleField>

                <Box w="100%"> </Box>
              </Flex>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.generalStep.taxcode",
                })}
              >
                <FormControl isInvalid={!!errors.taxcode}>
                  <Input {...register("taxcode", { required: false })} />
                  <FormattedErrorMessage fieldError={errors.taxcode} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.generalStep.jobTitle",
                })}
              >
                <FormControl isInvalid={!!errors.jobTitle}>
                  <Input {...register("jobTitle", { required: false })} />
                  <FormattedErrorMessage fieldError={errors.jobTitle} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.generalStep.contactMethod",
                })}
                {...fieldRenderForSmallField}
              >
                <FormControl isInvalid={!!errors.contactMethod}>
                  <Controller
                    control={control}
                    name="contactMethod"
                    render={({ field: { value, onChange }, fieldState }) => (
                      <ReactSelect
                        isMulti
                        value={contactMethodsOptions?.filter(opt =>
                          (value ?? []).includes(opt.value ?? "")
                        )}
                        options={contactMethodsOptions}
                        onChange={newVal => onChange(newVal.map(option => option.value))}
                        isLoading={isFetchingContactMethods || isLoadingContactMethods}
                        styles={useGetReactSelectStyles(fieldState)}
                      />
                    )}
                  />
                  <FormattedErrorMessage fieldError={errors.contactMethod} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.generalStep.comment",
                })}
              >
                <FormControl isInvalid={!!errors.comment}>
                  <Textarea {...register("comment", { required: false })} size="sm" />
                  <FormattedErrorMessage fieldError={errors.comment} />
                </FormControl>
              </SingleField>
            </Flex>
          </Collapse>
        </Box>
      </Flex>

      <Flex justifyContent="center" mt={10}>
        <NextStepButton isLoading={waitBeforeNextStep} />
      </Flex>
    </Box>
  )
}
